import { useState, useEffect } from "react";
import moment from "moment-timezone";
import { detectLiquiditySweeps } from "./detectLiquiditySweeps";

export const useLiquiditySweepData = (currencyPair, timeInterval, timeZone, gapThreshold, setMaxDifference, dateRange, setDateRange, availableDates, setAvailableDates, currentPrice, setCurrentPrice) => {
  const [rawData, setRawData] = useState(null); // Store raw data from API
  const [chartData, setChartData] = useState(null); // Store prepared chart data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch("https://vidplay-api.deng-software.com/fx", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ currencyPair, timeInterval }),
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setRawData(data.data); // Save raw data for processing in another useEffect
      } catch (err) {
        setError(`Failed to fetch chart data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [currencyPair, timeInterval]); // Trigger on changes to currencyPair or timeInterval

  // Process and prepare chart data
  useEffect(() => {
    if (!rawData) return;

    try {
      const processedData = rawData
        .map((point) => ({
          time: moment.tz(point.time, "UTC").tz(timeZone).format("YYYY-MM-DD HH:mm:ss"),
          open: point.open,
          high: point.high,
          low: point.low,
          close: point.close,
        }))
        .sort((a, b) => new Date(a.time) - new Date(b.time));

      // Set the current price
      const latestDataPoint = processedData[processedData.length - 1];
      if (latestDataPoint) {
        setCurrentPrice(latestDataPoint.close);
      }


      // Set available dates based on selected timeZone
      if (processedData.length > 0) {
        setAvailableDates({
          start: moment.tz(processedData[0]?.time, "UTC").tz(timeZone).format("YYYY-MM-DDTHH:mm:ss"),
          end: moment.tz(processedData[processedData.length - 1]?.time, "UTC").tz(timeZone).format("YYYY-MM-DDTHH:mm:ss"),
        });
      }

      const { liquiditySweeps } = detectLiquiditySweeps(currencyPair, processedData, gapThreshold, dateRange);

      // Set the initial date range to cover the processed data if not already set
      if (!dateRange.start || !dateRange.end) {
        setDateRange({
          start: processedData[0]?.time,
          end: processedData[processedData.length - 1]?.time,
        });
      }


      // Calculate the maximum sweep difference
      const maxDifference = liquiditySweeps.reduce(
        (max, sweep) => Math.max(max, Math.abs(sweep.difference)),
        0
      );
      setMaxDifference(maxDifference);

      // Add breach percentage calculation
      liquiditySweeps.forEach((sweep) => {
        sweep.breachPercentage = ((sweep.reversalClose - sweep.breachLevel) / sweep.breachLevel) * 100;
      });

      // Separate bullish and bearish sweeps
      const bullishSweeps = liquiditySweeps.filter((sweep) => sweep.direction === "bullish");
      const bearishSweeps = liquiditySweeps.filter((sweep) => sweep.direction === "bearish");

      const traceCandlesticks = {
        x: processedData.map((point) => point.time),
        open: processedData.map((point) => point.open),
        high: processedData.map((point) => point.high),
        low: processedData.map((point) => point.low),
        close: processedData.map((point) => point.close),
        type: "candlestick",
        name: `${currencyPair} Candlesticks`,
      };

      const traceBullishSweeps = {
        x: bullishSweeps.map((sweep) => sweep.time),
        y: bullishSweeps.map((sweep) => sweep.breachLevel),
        mode: "markers+text",
        marker: { color: "green", size: 10, symbol: "triangle-up" },
        text: bullishSweeps.map((sweep) => `<b>${sweep.difference}</b>`),
        textposition: "top center",
        name: `Bullish Sweeps (${bullishSweeps.length})`,
        hovertext: bullishSweeps.map(
          (sweep) =>
            `Time: ${moment(sweep.time).format("HH:mm:ss")}<br>Data: ${moment(sweep.time).format(
              "YYYY-MM-DD"
            )}<br>Breach Level: ${sweep.breachLevel}<br>Reversal Close: ${sweep.reversalClose}<br>Difference: ${sweep.difference
            }<br>Breach %: ${sweep.breachPercentage.toFixed(2)}%`
        ),
        hoverinfo: "text",
      };

      const traceBearishSweeps = {
        x: bearishSweeps.map((sweep) => sweep.time),
        y: bearishSweeps.map((sweep) => sweep.breachLevel),
        mode: "markers+text",
        marker: { color: "red", size: 10, symbol: "triangle-down" },
        text: bearishSweeps.map((sweep) => `<b>${sweep.difference}</b>`),
        textposition: "top center",
        name: `Bearish Sweeps (${bearishSweeps.length})`,
        hovertext: bearishSweeps.map(
          (sweep) =>
            `Time: ${moment(sweep.time).format("HH:mm:ss")}<br>Data: ${moment(sweep.time).format(
              "YYYY-MM-DD"
            )}<br>Breach Level: ${sweep.breachLevel}<br>Reversal Close: ${sweep.reversalClose}<br>Difference: ${sweep.difference
            }<br>Breach %: ${sweep.breachPercentage.toFixed(2)}%`
        ),
        hoverinfo: "text",
      };

      const layout = {
        xaxis: {
          title: `Time (${timeZone})`,
          type: "date",
          tickformat: "%b %d %H:%M",
          range: [dateRange.start, dateRange.end], // Use the date range from state
        },
        yaxis: { title: "Price", autorange: true },
        hovermode: "closest",
      };

      const chartInfo = {
        traces: [traceCandlesticks, traceBullishSweeps, traceBearishSweeps],
        layout,
      };

      setChartData(chartInfo);
    } catch (err) {
      setError(`Failed to process chart data: ${err.message}`);
    }
  }, [rawData, currencyPair, timeZone, gapThreshold, dateRange]); // Trigger on changes to rawData or processing dependencies

  return { chartData, loading, error, availableDates };
};
